import React from 'react';
import cx from 'classnames';

import { Checkbox } from '@trqk/components';

import { CardWrapper } from '..';

/**
 * @typedef {TourCardProps}
 * @property {JSX.Element} body main content of the card
 * @property {boolean} checked whether this card is checked off
 * @property {JSX.Element} footer optional footer of the card
 * @property {string} grayIconUrl an image URL for a grayscale icon generated by an `import` statement
 * @property {string} iconUrl an image URL for an icon generated by an `import` statement
 * @property {number} iconWidth width for the icon image
 * @property {number} step the number of the step in the tour
 * @property {string} title a title for the step in the tour
 */
interface TourCardProps {
    body: JSX.Element;
    checked?: boolean;
    footer?: JSX.Element;
    grayIconUrl: string;
    iconUrl: string;
    iconWidth: number;
    step: number;
    title: string;
}

const iconContainerHeight = 125;
const stepWidth = 40;

/**
 * Renders an Insights Card that represents a step in the guided tour of the app.
 * @param {TourCardProps} props
 */
export const TourCard = ({
    body,
    checked = false,
    footer,
    grayIconUrl,
    iconUrl,
    iconWidth,
    step,
    title,
}: TourCardProps): JSX.Element => (
        <CardWrapper addClasses="flex flex-col items-center">
            <div className="mt-3 flex w-full justify-end">
                <Checkbox
                    disabled={true}
                    checked={checked}
                    inputId={title}
                    size="large"
                />
            </div>
            <div className="mt-16" style={{ height: iconContainerHeight }}>
                {!checked && (
                    <img
                        src={iconUrl}
                        alt={`${title} icon`}
                        width={iconWidth}
                    />
                )}
                {checked && (
                    <img
                        src={grayIconUrl}
                        alt={`${title} icon`}
                        width={iconWidth}
                    />
                )}
            </div>
            <div className="my-4">
                <div
                    className={cx(
                        'rounded-full flex items-center justify-center',
                        {
                            'bg-black': !checked,
                            'bg-tertiary-300': checked,
                        },
                    )}
                    style={{ width: stepWidth, height: stepWidth }}
                >
                    <div className="text-white font-bold text-lg">{step}</div>
                </div>
            </div>
            <h4
                className={cx({
                    'text-tertiary-400': checked,
                })}
            >
                {title}
            </h4>
            <div
                className={cx('mt-2 mx-10', {
                    'text-tertiary-400': checked,
                })}
            >
                {body}
            </div>
            <div
                className={cx('flex-grow flex flex-col justify-end mb-8', {
                    'text-tertiary-400': checked,
                })}
            >
                {footer}
            </div>
        </CardWrapper>
    );
